/**
 * variant-group.js
 * =================
 * History API navigation for Bizdesk variant group pickers.
 *
 * Uses Shopify's Section Rendering API (?sections=) to fetch the
 * entire product section as server-rendered HTML and replace it in
 * the DOM — this handles price, images, description, ATC form,
 * extended properties, and all Shopify blocks correctly without
 * fighting theme JavaScript.
 *
 * Judge.me review widgets live in their own section (app block), so
 * that section is fetched in the same request and the widget is
 * re-initialised with Judge.me's own ReviewWidgetManager. The older
 * (non-revamp) Judge.me widget cannot be re-initialised cleanly, so
 * stores using it fall back to a normal page load.
 *
 * URL is updated via history.pushState so the link is shareable and
 * the browser back/forward buttons work.
 * Falls back to window.location.href on any error.
 */

(function () {
  'use strict';

  // ─── Module-level state ───────────────────────────────────────────────────────

  var popstateRegistered = false;   // guard: only add popstate listener once
  var isNavigating = false;         // prevent duplicate navigation on rapid clicks

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function getHandleFromPath(path) {
    var match = (path || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getProductSectionEl(container) {
    // Walk UP from the picker container to find the shopify-section wrapper.
    // On back/forward (no container passed) use the picker currently on the page,
    // since section ids like "template--123__main" don't contain "product".
    if (!container) container = document.querySelector('[data-vg-picker]');
    return container
      ? container.closest('[id^="shopify-section-"]')
      : document.querySelector('[id^="shopify-section-"][id*="product"]');
  }

  function getSectionId(container, sectionEl) {
    // The Liquid snippet writes section.id onto the picker as data-vg-section-id.
    // This is the definitive section ID registered with Shopify — no guessing needed.
    var fromAttr = container && container.getAttribute('data-vg-section-id');
    if (fromAttr) return fromAttr;
    // Fallback: derive from the DOM element ID
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : 'main-product';
  }

  // ─── Judge.me review widget support ──────────────────────────────────────────
  // The Judge.me review widget is an app block in its OWN section, so swapping
  // only the product section leaves the old product's reviews on screen.
  // We fetch that section too, re-run its inline data script, and start the
  // widget with Judge.me's own ReviewWidgetManager (newer "revamp" widget only).

  var jdgmManagerPromise = null;

  function getJudgeMeSectionIds(mainSectionEl) {
    var ids = [];
    document.querySelectorAll('.jdgm-review-widget').forEach(function (w) {
      var sec = w.closest('[id^="shopify-section-"]');
      if (!sec || sec === mainSectionEl) return;
      var id = sec.id.replace('shopify-section-', '');
      if (ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function isJudgeMeRevamp() {
    return !!(window.jdgmSettings && window.jdgmSettings.review_widget_revamp_enabled);
  }

  // Scripts inserted via innerHTML never run - recreate them so they execute.
  // (Judge.me's section has an inline script that sets jdgm.data.reviewWidget[productId].)
  function runInlineScripts(rootEl) {
    rootEl.querySelectorAll('script').forEach(function (oldScript) {
      if (oldScript.src) return;
      var s = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        s.setAttribute(oldScript.attributes[i].name, oldScript.attributes[i].value);
      }
      s.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(s, oldScript);
    });
  }

  // Load Judge.me's ReviewWidgetManager from the same chunk their
  // review_widget.js already imported (file names are hashed, so read it from there).
  function loadJudgeMeManager() {
    if (jdgmManagerPromise) return jdgmManagerPromise;

    var entry = Array.prototype.find.call(document.scripts, function (s) {
      return /\/review_widget\.js(\?|$)/.test(s.src);
    });
    if (!entry) return Promise.reject(new Error('Judge.me review_widget.js not found'));
    var entrySrc = entry.src;

    jdgmManagerPromise = fetch(entrySrc)
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        var m = txt.match(/\{\s*ReviewWidgetManager\s+as\s+\w+\s*\}\s*from\s*["']([^"']+)["']/);
        if (!m) throw new Error('ReviewWidgetManager import not found');
        return import(new URL(m[1], entrySrc).href);
      })
      .then(function (mod) {
        if (!mod.ReviewWidgetManager) throw new Error('ReviewWidgetManager not exported');
        return mod.ReviewWidgetManager;
      });

    jdgmManagerPromise.catch(function () { jdgmManagerPromise = null; });
    return jdgmManagerPromise;
  }

  function swapJudgeMeSections(data, ids) {
    return Promise.all(ids.map(function (id) {
      var html = data[id];
      var el = document.getElementById('shopify-section-' + id);
      if (!html || !el) return Promise.resolve();

      var temp = document.createElement('div');
      temp.innerHTML = html;
      var newEl = temp.querySelector('[id^="shopify-section-"]');
      el.innerHTML = newEl ? newEl.innerHTML : html;

      runInlineScripts(el);

      var widgets = el.querySelectorAll('.jdgm-review-widget[data-entry-point]');
      if (!widgets.length) return Promise.resolve();

      return loadJudgeMeManager().then(function (Manager) {
        return Promise.all(Array.prototype.map.call(widgets, function (w) {
          return new Manager(w).initialize();
        }));
      });
    }));
  }

  // ─── Section Rendering ────────────────────────────────────────────────────────

  /**
   * Fetch the new product's section HTML via Shopify's Section Rendering API
   * and swap it into the page. Re-initialises the picker in the new HTML.
   * Also refreshes any Judge.me review widget sections for the new product.
   *
   * @param {string}  handle            Shopify product handle to navigate to
   * @param {string}  targetUrl         Full URL of the member being navigated to
   * @param {boolean} [updateUrl]       Whether to push a history state (default true)
   * @param {Element} [sourceContainer] Picker that triggered the navigation
   *                                    (null = auto-detect section from DOM)
   */
  function navigateBySection(handle, targetUrl, updateUrl, sourceContainer) {
    if (isNavigating) return;
    isNavigating = true;

    var sectionEl = getProductSectionEl(sourceContainer);
    var sectionId = getSectionId(sourceContainer, sectionEl);
    var reviewSectionIds = getJudgeMeSectionIds(sectionEl);

    // The older (non-revamp) Judge.me widget can't be re-initialised cleanly,
    // so fall back to a normal page load to keep reviews correct.
    if (reviewSectionIds.length && !isJudgeMeRevamp()) {
      isNavigating = false;
      window.location.href = targetUrl;
      return;
    }

    // Section Rendering API allows up to 5 sections per request.
    var sectionsParam = [sectionId].concat(reviewSectionIds).slice(0, 5)
      .map(encodeURIComponent).join(',');

    fetch('/products/' + handle + '?sections=' + sectionsParam)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) throw new Error('section "' + sectionId + '" not in response');

        // Update URL before swapping DOM so the new page's data attributes
        // (current-gid etc.) match the URL the customer sees.
        if (updateUrl !== false) {
          history.pushState(
            { vgNavigated: true, handle: handle, url: targetUrl },
            '',
            targetUrl
          );
        }

        // Parse and replace the section content
        var temp = document.createElement('div');
        temp.innerHTML = html;

        // The response wraps the content in the section div — get its inner HTML
        var newSectionEl = temp.querySelector('[id^="shopify-section-"]');
        var newContent   = newSectionEl ? newSectionEl.innerHTML : html;

        if (sectionEl) {
          sectionEl.innerHTML = newContent;

          // Dispatch Shopify's official section:load event so the theme's own
          // JavaScript (image gallery, quantity input, zoom, etc.) reinitialises
          // on the new DOM elements. Dawn and all OS 2.0 themes listen for this.
          // Themes that don't listen still work — product data is always correct.
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: sectionId, targetId: sectionEl.id }
          }));

          // Re-initialise our picker on the new content
          sectionEl.querySelectorAll('[data-vg-picker]').forEach(initPicker);

          // Refresh the Judge.me review widget(s) for the new product.
          // Returning the promise means a failure falls into the .catch below
          // (full page load), and isNavigating stays locked until it's done.
          if (reviewSectionIds.length) {
            return swapJudgeMeSections(data, reviewSectionIds);
          }
        } else {
          // Section wrapper not found — replace body content as last resort
          document.body.innerHTML = html;
          document.querySelectorAll('[data-vg-picker]').forEach(initPicker);
        }

      })
      .catch(function (err) {
        console.warn('variant-group.js: section render failed, full reload', err);
        window.location.href = targetUrl;
      })
      .finally(function () {
        isNavigating = false;
      });
  }

  // ─── Availability helpers ─────────────────────────────────────────────────────

  function getAvailableValues(members, axisIndex, axisCount, selection) {
    var available = {};
    members.forEach(function (m) {
      var value = m.v[axisIndex];
      if (available[value]) return;
      var compatible = true;
      for (var j = 0; j < axisCount; j++) {
        if (j === axisIndex) continue;
        if (selection[j] !== null && m.v[j] !== selection[j]) {
          compatible = false;
          break;
        }
      }
      if (compatible) available[value] = true;
    });
    return available;
  }

  function findExactMatch(members, selection) {
    for (var i = 0; i < members.length; i++) {
      var m = members[i];
      var allMatch = true;
      for (var j = 0; j < selection.length; j++) {
        if (selection[j] !== null && m.v[j] !== selection[j]) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) return m;
    }
    return null;
  }

  function findBestMatch(members, selection, changedAxisIndex) {
    var exact = findExactMatch(members, selection);
    if (exact) return exact;
    if (changedAxisIndex !== undefined) {
      var changedValue = selection[changedAxisIndex];
      for (var i = 0; i < members.length; i++) {
        if (members[i].v[changedAxisIndex] === changedValue) return members[i];
      }
    }
    return null;
  }

  // ─── Per-picker init ──────────────────────────────────────────────────────────

  function initPicker(container) {

    var jsonEl = document.getElementById('vg-data-json');
    if (!jsonEl) return;

    var vgData;
    try { vgData = JSON.parse(jsonEl.textContent || jsonEl.innerHTML); }
    catch (e) { console.warn('variant-group.js: could not parse vg-data-json', e); return; }

    var members   = vgData.m    || [];
    var axisCount = (vgData.axes || []).length;
    var currentGid = container.getAttribute('data-current-gid');

    if (!members.length) return;

    // Sort by price ascending
    members = members.slice().sort(function (a, b) {
      return parseFloat(a.p) - parseFloat(b.p);
    });

    // Starting selection from current product
    var selection = new Array(axisCount).fill(null);
    members.forEach(function (m) {
      if (m.id === currentGid && Array.isArray(m.v)) selection = m.v.slice();
    });

    // ── Axis label sync ──────────────────────────────────────────────────────

    function updateAxisLabels(member) {
      if (!Array.isArray(member.v)) return;
      member.v.forEach(function (value, i) {
        var el = container.querySelector('[data-vg-axis-label="' + i + '"]');
        if (el) el.textContent = ' ' + value;
      });
    }

    // ── Disabled state ───────────────────────────────────────────────────────

    function updateDisabledStates() {
      for (var ai = 0; ai < axisCount; ai++) {
        var available = getAvailableValues(members, ai, axisCount, selection);

        var dropdown = container.querySelector('[data-vg-dropdown="' + ai + '"]');
        if (dropdown) {
          for (var oi = 0; oi < dropdown.options.length; oi++) {
            dropdown.options[oi].disabled = !available[dropdown.options[oi].text.trim()];
          }
        }

        container.querySelectorAll('[data-vg-bubble="' + ai + '"]').forEach(function (bubble) {
          var reachable = !!available[bubble.getAttribute('data-value')];
          bubble.classList.toggle('vg-picker__bubble--disabled', !reachable);
          bubble.setAttribute('aria-disabled', reachable ? 'false' : 'true');
        });
      }
    }

    // ── Axis selection handler ───────────────────────────────────────────────

    function onAxisSelect(axisIndex, selectedValue) {
      selection[axisIndex] = selectedValue;
      var match = findBestMatch(members, selection, axisIndex);

      if (match) {
        if (match.id !== currentGid) {
          // Navigate to the matched member using Section Rendering API
          var handle = getHandleFromPath(match.u);
          if (handle) {
            navigateBySection(handle, match.u, true, container);
          } else {
            window.location.href = match.u;
          }
        } else {
          updateAxisLabels(match);
          updateDisabledStates();
        }
      }
    }

    // ── Wire up bubbles ──────────────────────────────────────────────────────

    container.querySelectorAll('[data-vg-bubble]').forEach(function (bubble) {
      var axisIndex = parseInt(bubble.getAttribute('data-vg-bubble'), 10);
      var value     = bubble.getAttribute('data-value');

      bubble.addEventListener('click', function (e) {
        e.preventDefault();
        if (bubble.classList.contains('vg-picker__bubble--disabled')) return;
        onAxisSelect(axisIndex, value);
      });
    });

    // ── Wire up dropdowns ────────────────────────────────────────────────────

    container.querySelectorAll('[data-vg-dropdown]').forEach(function (select) {
      var axisIndex = parseInt(select.getAttribute('data-vg-dropdown'), 10);
      select.addEventListener('change', function () {
        onAxisSelect(axisIndex, select.options[select.selectedIndex].text.trim());
      });
    });

    // ── Initialise on load ───────────────────────────────────────────────────

    var currentMember = members.find(function (m) { return m.id === currentGid; });
    if (currentMember) updateAxisLabels(currentMember);
    updateDisabledStates();
  }

  // ─── Popstate (browser back / forward) ───────────────────────────────────────

  function handlePopState(event) {
    var state = event.state;
    if (!state || !state.vgNavigated) {
      // Not one of our history entries — full reload to restore correct state
      window.location.reload();
      return;
    }
    var handle = state.handle || getHandleFromPath(window.location.pathname);
    var url    = state.url    || window.location.href;
    if (handle) {
      navigateBySection(handle, url, false, null);  // null = auto-detect section from DOM
    } else {
      window.location.reload();
    }
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  function boot() {
    // Register popstate listener once — survives section re-renders because
    // it is on window, not on any DOM element that gets replaced
    if (!popstateRegistered) {
      window.addEventListener('popstate', handlePopState);
      popstateRegistered = true;
    }

    // Record initial state for back-button support
    var initialHandle = getHandleFromPath(window.location.pathname);
    history.replaceState(
      { vgNavigated: true, handle: initialHandle, url: window.location.href },
      '',
      window.location.href
    );

    document.querySelectorAll('[data-vg-picker]').forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();